import { useEffect, useState } from "react";
import { supabase } from "../lib/supabase";
import { categorizeProduct } from "../lib/categorizeProduct";
import { isHeicFile, isWebpFile, isUnsupportedUploadFile, UNSUPPORTED_FORMAT_MESSAGE, convertHeicToJpeg } from "../utils/heicConvert";
import { resizeImageIfNeeded } from "../utils/imageResize";
import VariantForm, { ProductImage, Variant } from "./VariantForm";
import { buildThumbnailBlob } from "../utils/thumbnail";

/**
 * Panel "Editar producto" para Mi marca → Mis productos. Espejo de
 * `AddProductFlow`, pero en modo edición: monta `VariantForm` con un
 * `editingVariant` (desktop = panel lateral derecho, mobile/PWA = bottom-sheet,
 * ambos resueltos dentro de `VariantForm`) y porta la MISMA lógica de Supabase
 * que ya usa la pantalla vieja (`SeleccionarProductosAnuncio`):
 *   • renombrar la variante (+ webhook de categorización, modo "editar"),
 *   • subir imagen (HEIC → jpeg, resize, thumbnail opt),
 *   • borrar imagen (storage full+opt → fila), garantizando una principal,
 *   • marcar principal,
 *   • eliminar el producto en cascada (storage → imágenes → variante → producto).
 *
 * `product` es la variante a editar (id + nombre); sus imágenes se cargan al
 * abrir. Al guardar/eliminar refresca la grilla (`onSaved`) y cierra (`onClose`).
 */
export const EditProductFlow = ({
  companyId,
  product,
  onClose,
  onSaved,
}: {
  companyId: string | null;
  product: { id: string; name: string };
  onClose: () => void;
  onSaved: () => Promise<void> | void;
}) => {
  const [editingVariant, setEditingVariant] = useState<Variant | null>(null);
  const [saving, setSaving] = useState(false);
  const [imageError, setImageError] = useState<string | null>(null);
  const [uploadingImages, setUploadingImages] = useState<
    Record<string, boolean>
  >({});
  // VariantForm exige estos props aunque en modo edición no se usan (son del
  // alta). Mantenemos un placeholder estable.
  const [newProduct, setNewProduct] = useState<{
    nombre: string;
    images: ProductImage[];
  }>({ nombre: "", images: [] });

  // Cargar las imágenes de la variante al abrir → armar el editingVariant.
  useEffect(() => {
    if (!companyId) return;
    let cancelled = false;
    (async () => {
      const { data } = await supabase
        .from("productos_imagenes")
        .select("id, url, principal, orden, variante_id")
        .eq("variante_id", product.id)
        .eq("id_company", companyId)
        .order("orden", { ascending: true });
      const images: ProductImage[] = (data || []).map((img: any) => ({
        id: img.id,
        url: img.url,
        principal: img.principal,
        orden: img.orden,
        producto_variante_id: 0,
        variante_id: img.variante_id,
      }));
      if (!cancelled)
        setEditingVariant({ id: product.id, nombre: product.name, images });
    })();
    return () => {
      cancelled = true;
    };
  }, [companyId, product.id, product.name]);

  // ── Guardar nombre (+ webhook categorización modo "editar") ──────────────
  const handleSave = async () => {
    if (!editingVariant || !companyId) return;
    const name = (editingVariant.nombre || "").trim();
    if (!name) return; // VariantForm ya valida, doble chequeo defensivo
    try {
      setSaving(true);
      setImageError(null);

      const { error: updError } = await supabase
        .from("variantes")
        .update({ nombre: name })
        .eq("id", editingVariant.id)
        .eq("id_company", companyId);
      if (updError) throw updError;

      // Categorización IA tras editar la variante (best-effort).
      try {
        const {
          data: { user },
        } = await supabase.auth.getUser();
        const { data: varRow } = await supabase
          .from("variantes")
          .select("product_id")
          .eq("id", editingVariant.id)
          .single();
        const editProductId = (varRow as any)?.product_id;
        if (user && editProductId) {
          categorizeProduct(String(editProductId), {
            modo: "editar",
            userId: user.id,
          });
        }
      } catch {
        /* noop */
      }

      await onSaved();
      onClose();
    } catch (err) {
      console.error("[EditProductFlow] save error:", err);
      setImageError(
        err instanceof Error ? err.message : "Error al guardar el producto",
      );
    } finally {
      setSaving(false);
    }
  };

  // ── Subir imagen (HEIC → jpeg, resize, thumbnail opt) ────────────────────
  const handleImageUpload = async (variantId: string, file: File) => {
    if (!companyId) return;
    try {
      setUploadingImages((prev) => ({ ...prev, [String(variantId)]: true }));
      setImageError(null);

      const MAX_FILE_SIZE = 100 * 1024 * 1024; // 100 MB
      if (file.size > MAX_FILE_SIZE) {
        setImageError(
          "El archivo supera el límite de 100 MB. Reducí el tamaño e intentá de nuevo.",
        );
        return;
      }

      // Formatos bloqueados (RAW/TIFF/GIF/SVG — ADR-590): rebotan con
      // mensaje claro, mismo patrón que el límite de tamaño.
      if (isUnsupportedUploadFile(file)) {
        setImageError(UNSUPPORTED_FORMAT_MESSAGE);
        return;
      }

      let processedFile: File = file;
      if (isHeicFile(file) || isWebpFile(file)) {
        try {
          const converted = await convertHeicToJpeg(file, 0.8);
          processedFile = converted.file;
        } catch {
          throw new Error(
            "No pudimos convertir la imagen HEIC. Probá con una imagen en formato png o jpg",
          );
        }
      }
      processedFile = await resizeImageIfNeeded(processedFile, 2000, 0.85);

      const timestamp = Date.now();
      const sanitizedName = processedFile.name.replace(/[^a-zA-Z0-9._-]/g, "_");
      const fileBaseName = `${timestamp}_${sanitizedName}`;

      const fullPath = `${companyId}/${variantId}/full/${fileBaseName}`;
      const { error: fullErr } = await supabase.storage
        .from("productos")
        .upload(fullPath, processedFile);
      if (fullErr) throw fullErr;

      // Thumbnail optimizado vía canvas (best-effort).
      try {
        const blob = await buildThumbnailBlob(processedFile);
        if (!blob) throw new Error("no se pudo generar el thumbnail");
        const optPath = `${companyId}/${variantId}/opt/${fileBaseName}`;
        const { error: optErr } = await supabase.storage
          .from("productos")
          .upload(optPath, blob);
        if (optErr)
          console.warn("Optimized upload failed, continuing with full", optErr);
      } catch (optErr) {
        console.warn("Error creating optimized image, continuing", optErr);
      }

      const makePrincipal =
        editingVariant?.id === variantId &&
        !(editingVariant.images || []).some((i) => i.principal);
      const { error: imgInsertError } = await supabase
        .from("productos_imagenes")
        .insert({
          id_company: companyId,
          variante_id: variantId,
          url: fileBaseName,
          principal: !!makePrincipal,
          orden: (editingVariant?.images?.length || 0) + 1,
        });
      if (imgInsertError) throw imgInsertError;

      await refreshVariantImages(variantId);
    } catch (err) {
      console.error("[EditProductFlow] upload error:", err);
      setImageError(
        err instanceof Error ? err.message : "Error al subir la imagen",
      );
    } finally {
      setUploadingImages((prev) => ({ ...prev, [String(variantId)]: false }));
    }
  };

  // ── Borrar imagen (storage full+opt → fila), dejando una principal ───────
  const handleDeleteImage = async (variantId: string, imageId: number) => {
    if (!companyId) return;
    try {
      const { data: imageData } = await supabase
        .from("productos_imagenes")
        .select("id, url")
        .eq("id", imageId)
        .eq("id_company", companyId)
        .single();
      const filename = (imageData as any)?.url || "";
      if (filename && !filename.startsWith("http")) {
        await supabase.storage
          .from("productos")
          .remove([
            `${companyId}/${variantId}/full/${filename}`,
            `${companyId}/${variantId}/opt/${filename}`,
          ]);
      }

      await supabase
        .from("productos_imagenes")
        .delete()
        .eq("id", imageId)
        .eq("id_company", companyId);

      // Garantizar que quede una principal.
      const { data: remaining } = await supabase
        .from("productos_imagenes")
        .select("id, principal")
        .eq("variante_id", variantId)
        .eq("id_company", companyId)
        .order("orden", { ascending: true });
      if (
        (remaining || []).length > 0 &&
        !(remaining || []).some((i: any) => i.principal)
      ) {
        await supabase
          .from("productos_imagenes")
          .update({ principal: true })
          .eq("id", (remaining as any)[0].id)
          .eq("id_company", companyId);
      }

      await refreshVariantImages(variantId);
    } catch (err) {
      console.error("[EditProductFlow] delete image error:", err);
      setImageError(
        err instanceof Error ? err.message : "Error al eliminar la imagen",
      );
    }
  };

  // ── Marcar principal ─────────────────────────────────────────────────────
  const handleSetPrincipalImage = async (
    variantId: string,
    imageId: number,
  ) => {
    if (!companyId) return;
    try {
      await supabase
        .from("productos_imagenes")
        .update({ principal: false })
        .eq("variante_id", variantId)
        .eq("id_company", companyId);
      await supabase
        .from("productos_imagenes")
        .update({ principal: true })
        .eq("id", imageId)
        .eq("id_company", companyId);
      await refreshVariantImages(variantId);
    } catch (err) {
      console.error("[EditProductFlow] set principal error:", err);
      setImageError(
        err instanceof Error
          ? err.message
          : "Error al actualizar la imagen principal",
      );
    }
  };

  // Reorder: no implementado en la pantalla vieja tampoco (placeholder).
  const handleReorderImages = async () => {
    return;
  };

  // ── Eliminar producto (cascade storage → imágenes → variante → producto) ──
  const handleDeleteVariant = async (variantId: string) => {
    if (!companyId) return;
    try {
      setSaving(true);
      setImageError(null);

      const { data: variantRow, error: variantSelErr } = await supabase
        .from("variantes")
        .select("id, product_id")
        .eq("id", variantId)
        .eq("id_company", companyId)
        .single();
      if (variantSelErr) throw variantSelErr;
      const productId = (variantRow as any)?.product_id as string | null;

      const { data: imagesData } = await supabase
        .from("productos_imagenes")
        .select("id, url")
        .eq("variante_id", variantId)
        .eq("id_company", companyId);
      const filesToRemove: string[] = [];
      (imagesData || []).forEach((img: any) => {
        const filename = (img?.url as string) || "";
        if (filename && !filename.startsWith("http")) {
          filesToRemove.push(`${companyId}/${variantId}/full/${filename}`);
          filesToRemove.push(`${companyId}/${variantId}/opt/${filename}`);
        }
      });
      if (filesToRemove.length > 0) {
        try {
          await supabase.storage.from("productos").remove(filesToRemove);
        } catch {
          /* best-effort */
        }
      }

      await supabase
        .from("productos_imagenes")
        .delete()
        .eq("variante_id", variantId)
        .eq("id_company", companyId);

      await supabase
        .from("variantes")
        .delete()
        .eq("id", variantId)
        .eq("id_company", companyId);

      // Si el producto se queda sin variantes → borrarlo también.
      if (productId) {
        const { data: otherVariants } = await supabase
          .from("variantes")
          .select("id")
          .eq("product_id", productId)
          .eq("id_company", companyId)
          .limit(1);
        if (!otherVariants || otherVariants.length === 0) {
          try {
            await supabase
              .from("productos")
              .delete()
              .eq("id", productId)
              .eq("id_company", companyId);
          } catch {
            /* best-effort */
          }
        }
      }

      await onSaved();
      onClose();
    } catch (err) {
      console.error("[EditProductFlow] delete product error:", err);
      setImageError(
        err instanceof Error ? err.message : "Error al eliminar el producto",
      );
    } finally {
      setSaving(false);
    }
  };

  // Re-lee las imágenes de la variante y actualiza el editingVariant del panel.
  const refreshVariantImages = async (variantId: string) => {
    if (!companyId) return;
    const { data } = await supabase
      .from("productos_imagenes")
      .select("id, url, principal, orden, variante_id")
      .eq("variante_id", variantId)
      .eq("id_company", companyId)
      .order("orden", { ascending: true });
    const updatedImages: ProductImage[] = (data || []).map((img: any) => ({
      id: img.id,
      url: img.url,
      principal: img.principal,
      orden: img.orden,
      producto_variante_id: 0,
      variante_id: img.variante_id,
    }));
    setEditingVariant((prev) =>
      prev && prev.id === variantId ? { ...prev, images: updatedImages } : prev,
    );
  };

  // Mientras cargan las imágenes (editingVariant null) no montamos el form.
  if (!editingVariant) return null;

  return (
    <VariantForm
      editingVariant={editingVariant}
      newProduct={newProduct}
      loading={saving}
      uploadingImages={uploadingImages}
      imageError={imageError}
      onCancel={onClose}
      onSave={handleSave}
      onImageUpload={handleImageUpload}
      onDeleteImage={handleDeleteImage}
      onSetPrincipalImage={handleSetPrincipalImage}
      onReorderImages={handleReorderImages}
      setEditingVariant={setEditingVariant}
      setNewProduct={setNewProduct}
      onDeleteVariant={handleDeleteVariant}
    />
  );
};

export default EditProductFlow;
